<template>
    <div class="card bt-page">
        <PageHeader
            title="Detail Budget Allocation"
            subtitle="Budget allocations on master budget lines, with versions and approval"
            btnTitle="Add Budget Allocation"
            :showBtn="false"
            :showAddNew="true"
            @onClickAddNewButton="onClickAddNew"
        />

        <div class="filter-section">
            <div class="filter-bar">
                <div class="filter-dates">
                    <div class="filter-date-field">
                        <label class="filter-label">From</label>
                        <input v-model="start" type="date" class="form-control filter-date-input">
                    </div>
                    <div class="filter-date-field">
                        <label class="filter-label">To</label>
                        <input v-model="end" type="date" class="form-control filter-date-input">
                    </div>
                    <button type="button" class="btn btn-primary filter-go-btn" :disabled="loading" @click="onClickGo">Go</button>
                </div>

                <div class="filter-bar-actions">
                    <span class="result-summary">{{ resultSummary }}</span>
                    <FilterPopover
                        ref="filterPopoverRef"
                        title="Filter Budget Allocations"
                        popoverWidth="360px"
                        :activeCount="activeFilterCount"
                        @open="onOpenFilter"
                        @apply="onApplyFilters"
                        @clear="onClearFilters"
                    >
                        <div class="col-12">
                            <label class="filter-label">Master Budget Line</label>
                            <v-select
                                v-model="draftFilters.project_id"
                                placeholder="Any master budget line"
                                :options="budgetLineOptions"
                                label="name"
                                :reduce="option => option.id"
                            />
                        </div>
                        <div class="col-12 mt-1">
                            <label class="filter-label">Status</label>
                            <v-select
                                v-model="draftFilters.status"
                                placeholder="Any status"
                                :options="statusOptions"
                                label="name"
                                :reduce="option => option.id"
                            />
                        </div>
                        <div class="col-12 mt-1">
                            <label class="filter-label">Search</label>
                            <input v-model="draftFilters.search" type="text" class="form-control" placeholder="Budget name or code">
                        </div>
                    </FilterPopover>
                </div>
            </div>

            <div v-if="activeFilterCount" class="filter-chips">
                <span v-for="chip in filterChips" :key="chip.key" class="filter-chip">
                    <span class="filter-chip-label">{{ chip.label }}:</span>
                    <span>{{ chip.value }}</span>
                    <button type="button" class="filter-chip-remove" title="Remove filter" @click="onRemoveFilter(chip.key)">
                        <i class="fa-solid fa-xmark"></i>
                    </button>
                </span>
            </div>
        </div>

        <div class="table-section">
            <BudgetTrackerTable
                :rows="budgets"
                :loading="loading"
                :exportingVersionId="exportingVersionId"
                @edit="onClickEdit"
                @add="onClickAddNew"
                @export="onExportVersion"
            />
        </div>

        <div class="pagination-section">
            <Pagination :offset="offset" @onPageChange="onPageChange"/>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/atom/PageHeader.vue'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import Pagination from '@/components/atom/Pagination'
import BudgetTrackerTable from '@/components/molecule/procurement/budget-tracker/BudgetTrackerTable.vue'
import handleBudgetTracker from '@/services/modules/procurement/budgetTracker'
import useBudgetVersionExport from '@/composables/useBudgetVersionExport'
import useAutoRefresh from '@/composables/useAutoRefresh'
import { getApiErrorMessage, resetPaginationPage } from '@/services/utils/global.js'

const $store = useStore()
const $route = useRoute()
const $router = useRouter()
const showError = inject('showError')
const { fetchTrackerBudgets, fetchBudgetLines } = handleBudgetTracker()
const { exportingVersionId, exportVersion } = useBudgetVersionExport(() => $route.params.companyId)

const emptyFilters = () => ({ project_id: null, status: null, search: '' })

const offset = ref(20)
const loading = ref(false)
const budgets = ref([])
const budgetLineOptions = ref([])
const pageMeta = ref({ from: 0, to: 0, total: 0 })
const start = ref('')
const end = ref('')
const filterPopoverRef = ref(null)
const draftFilters = ref(emptyFilters())
const appliedFilters = ref(emptyFilters())

const statusOptions = [
    { id: 'draft', name: 'Draft' },
    { id: 'pending', name: 'Pending' },
    { id: 'rejected', name: 'Rejected' },
    { id: 'active', name: 'Active' }
]

const companyId = computed(() => $route.params.companyId)

const optionName = (options, id) => {
    const found = options.find(option => option.id === id)
    if (! found) return '#' + id
    return found.name
}

const filterChips = computed(() => {
    const chips = []
    const filters = appliedFilters.value
    if (filters.project_id) chips.push({ key: 'project_id', label: 'Master Budget Line', value: optionName(budgetLineOptions.value, filters.project_id) })
    if (filters.status) chips.push({ key: 'status', label: 'Status', value: optionName(statusOptions, filters.status) })
    if (filters.search) chips.push({ key: 'search', label: 'Search', value: filters.search })
    return chips
})

const activeFilterCount = computed(() => filterChips.value.length)

const resultSummary = computed(() => {
    if (loading.value) return 'Loading…'
    if (! pageMeta.value.total) return 'No records'
    return `Showing ${pageMeta.value.from}–${pageMeta.value.to} of ${pageMeta.value.total}`
})

const getQuery = (page) => {
    const filters = appliedFilters.value
    let query = '?company_id=' + companyId.value
    query += '&offset=' + offset.value
    if (start.value && end.value) query += `&start_date=${start.value}&end_date=${end.value}`
    if (page) query += '&page=' + page
    if (filters.project_id) query += '&project_id=' + filters.project_id
    if (filters.status) query += '&status=' + filters.status
    if (filters.search) query += '&search=' + encodeURIComponent(filters.search)
    return query
}

const setPagination = (data) => {
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const clearList = () => {
    budgets.value = []
    pageMeta.value = { from: 0, to: 0, total: 0 }
    $store.commit('resetPagination')
}

const applyResponse = (res) => {
    if (! res.status) return clearList()
    budgets.value = res.data.data
    pageMeta.value = { from: res.data.from, to: res.data.to, total: res.data.total }
    setPagination(res.data)
}

let listRequestNo = 0

const getBudgets = async (page) => {
    listRequestNo++
    loading.value = true
    try {
        const res = await fetchTrackerBudgets(getQuery(page))
        applyResponse(res)
    } catch (err) {
        clearList()
        showError(getApiErrorMessage(err))
    } finally {
        loading.value = false
    }
}

const getBudgetLines = async () => {
    try {
        const res = await fetchBudgetLines('?company_id=' + companyId.value)
        if (! res.status) return
        budgetLineOptions.value = res.data
    } catch (err) {
        showError(getApiErrorMessage(err))
    }
}

const buildDateQuery = () => {
    const routeQuery = Object.assign({}, $route.query, { page: 1 })
    delete routeQuery.start
    delete routeQuery.end
    if (start.value) routeQuery.start = start.value
    if (end.value) routeQuery.end = end.value
    return routeQuery
}

const onClickGo = async () => {
    await $router.push({ path: $route.path, query: buildDateQuery() })
    await getBudgets(1)
}

const onPageChange = async (page) => {
    await $router.push({ path: $route.path, query: Object.assign({}, $route.query, { page }) })
    await getBudgets(page)
}

const onOpenFilter = () => {
    draftFilters.value = Object.assign({}, appliedFilters.value)
}

const onApplyFilters = async () => {
    appliedFilters.value = Object.assign({}, draftFilters.value)
    resetPaginationPage($store, $route, $router)
    await getBudgets(1)
}

const onRemoveFilter = async (key) => {
    appliedFilters.value = Object.assign({}, appliedFilters.value, { [key]: emptyFilters()[key] })
    resetPaginationPage($store, $route, $router)
    await getBudgets(1)
}

const onClearFilters = async () => {
    draftFilters.value = emptyFilters()
    appliedFilters.value = emptyFilters()
    if (filterPopoverRef.value) filterPopoverRef.value.close()
    resetPaginationPage($store, $route, $router)
    await getBudgets(1)
}

const onExportVersion = ({ budget, version }) => {
    exportVersion(budget.id, budget.name, version)
}

const refreshSilently = async () => {
    if (loading.value) return
    const startedAt = listRequestNo
    const res = await fetchTrackerBudgets(getQuery($route.query.page))
    if (startedAt !== listRequestNo) return
    if (! res.status) return
    applyResponse(res)
}

const hasWaitingVersion = () => budgets.value.some(budget => budget.versions.some(version => version.status === 'pending'))

useAutoRefresh(refreshSilently, hasWaitingVersion)

const onClickAddNew = () => {
    $router.push({ name: 'budget-tracker-add', params: $route.params, query: $route.query })
}

const onClickEdit = (budgetId) => {
    $router.push({
        name: 'budget-tracker-edit',
        params: Object.assign({}, $route.params, { budgetId }),
        query: $route.query
    })
}

const initFilters = () => {
    start.value = $route.query.start || ''
    end.value = $route.query.end || ''
}

onMounted(async () => {
    initFilters()
    await Promise.all([getBudgets($route.query.page), getBudgetLines()])
})
</script>

<style scoped>
.bt-page {
    min-height: calc(100vh - 158px);
}
.filter-section {
    padding: 18px 20px 0;
}
.filter-bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}
.filter-dates {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
}
.filter-date-field {
    display: flex;
    flex-direction: column;
}
.filter-label {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #344054;
}
.filter-date-input {
    width: 180px;
    height: 40px;
    border-radius: 8px;
    border-color: #e4e7ec;
    font-size: 14px;
}
.filter-go-btn {
    height: 40px;
    min-width: 76px;
    border-radius: 8px;
    font-size: 14px;
}
.filter-bar-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
}
.result-summary {
    font-size: 14px;
    color: #667085;
    white-space: nowrap;
}
.filter-chips {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
}
.filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 8px 5px 12px;
    border-radius: 16px;
    background: #f4f3ff;
    color: #4338ca;
    font-size: 13px;
}
.filter-chip-label {
    color: #7c78a8;
}
.filter-chip-remove {
    border: 0;
    background: transparent;
    color: #7c78a8;
    font-size: 12px;
    line-height: 1;
    padding: 2px;
    cursor: pointer;
}
.filter-chip-remove:hover {
    color: #4338ca;
}
.table-section {
    padding: 16px 20px 0;
}
.pagination-section {
    padding: 14px 20px 18px;
}
@media (max-width: 576px) {
    .filter-section {
        padding: 14px 16px 0;
    }
    .table-section {
        padding: 14px 16px 0;
    }
    .pagination-section {
        padding: 12px 16px 16px;
    }
    .filter-dates {
        width: 100%;
    }
    .filter-date-field {
        flex: 1 1 140px;
    }
    .filter-date-input {
        width: 100%;
    }
    .filter-bar-actions {
        width: 100%;
        justify-content: space-between;
        margin-left: 0;
    }
}
</style>
